'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Coins, Mountain, TreePine, Hammer } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function HuitDeniersPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Deniers</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Huit</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Deniers'e Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-xl p-8 mb-8 border border-green-600/30">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-green-100 to-emerald-50 border border-green-300 flex flex-col items-center justify-center relative">
                {/* Card Visual Design */}
                <div className="text-center">
                  <div className="text-6xl mb-4">🔨</div>
                  <div className="w-16 h-1 bg-green-600 mx-auto mb-4"></div>
                  <div className="text-2xl font-bold text-green-800">HUIT</div>
                  <div className="text-sm text-green-700 mt-2">de Deniers</div>
                </div>
                {/* Decorative elements */}
                <div className="absolute top-4 left-4 text-green-600">
                  <Hammer className="h-4 w-4" />
                </div>
                <div className="absolute top-4 right-4 text-green-600">
                  <Hammer className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 left-4 text-green-600">
                  <Coins className="h-4 w-4" />
                </div>
                <div className="absolute bottom-4 right-4 text-green-600">
                  <Coins className="h-4 w-4" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">8</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Huit de Deniers</h1>
                  <p className="text-xl text-green-400">Zanaatkarlık / Beceri Geliştirme</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Sekiz sikkenin simgelediği ustalık ve beceri geliştirme süreci. El emeği,
                zanaat sanatları ve sürekli öğrenme. Sabırla çalışarak ustalaşma yolunda
                ilerleme ve mükemmeliyetçilik.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Zanaatkarlık</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Beceri</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Ustalık</span>
                <span className="px-3 py-1 bg-green-600/20 text-green-300 rounded-full text-sm">Çalışkanlık</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Hammer className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Mesleki Beceri</h3>
                  <p className="text-slate-400 text-sm">El sanatları ve zanaat konularında gelişim.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sürekli Öğrenme</h3>
                  <p className="text-slate-400 text-sm">Kendini geliştirme ve yeni beceriler kazanma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Pratik Çalışma</h3>
                  <p className="text-slate-400 text-sm">Uygulamalı çalışma ve deneyim kazanma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Detaycılık</h3>
                  <p className="text-slate-400 text-sm">İşine özen gösterme, kaliteli üretim.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Mountain className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Mükemmeliyetçilik</h3>
                  <p className="text-slate-400 text-sm">Hiçbir zaman yeterli bulmama obsesyonu.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Beceri Eksikliği</h3>
                  <p className="text-slate-400 text-sm">Yetersiz eğitim, amatörlük.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Tembellik</h3>
                  <p className="text-slate-400 text-sm">Çalışmaya isteksizlik, özensizlik.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Verimsizlik</h3>
                  <p className="text-slate-400 text-sm">Zaman kaybı, sonuç odaklı çalışmama.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Hammer className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Zanaat Aletleri</h3>
              <p className="text-slate-400 text-sm">El becerisi ve pratik bilgi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <Coins className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Çalışma Masası</h3>
              <p className="text-slate-400 text-sm">Düzenli çalışma ve disiplin.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-3">
                <TreePine className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ustalık Yolculuğu</h3>
              <p className="text-slate-400 text-sm">Sürekli gelişim ve öğrenme süreci.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Mesleki Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Zanaat öğrenme</li>
                <li>• Teknik eğitim</li>
                <li>• Sertifika programları</li>
                <li>• Pratik deneyim</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <TreePine className="h-5 w-5 text-green-400" />
                Yaratıcı Projeler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• El sanatları</li>
                <li>• Hobi projeleri</li>
                <li>• Sanatsal çalışmalar</li>
                <li>• Yeni beceri kazanma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers/sept"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Sept de Deniers
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/deniers/neuf"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Neuf de Deniers
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}